import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
// import { usePathname } from 'next/navigation';

interface PowerPointViewProps {
  children?: ReactNode;
  /** Target element or element id to scale (e.g., body with id "ppt-stage"). */
  target?: string | HTMLElement;
  /** Baseline width for 16:9. Default 1366. */
  baselineWidth?: number;
  /** Baseline height for 16:9. Default 768. */
  baselineHeight?: number;
  // Removed preferZoom; always uses transform-scale for cross-browser consistency
}

// Scales the TARGET element to fit within its parent/viewport using a fixed 16:9 baseline, similar to PresentationView.
// Does not wrap children; it mutates target inline styles and restores them on unmount.
const PowerPointView: React.FC<PowerPointViewProps> = ({ target, baselineWidth = 1366, baselineHeight = 768 }) => {
  // const pathname = usePathname();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const observedElementRef = useRef<HTMLElement | null>(null);
  const measureElementRef = useRef<HTMLElement | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const originalMeasureStylesRef = useRef<{ backgroundColor?: string; display?: string; placeItems?: string; justifyContent?: string; alignItems?: string } | null>(null);
  const originalTargetStylesRef = useRef<{
    transform?: string;
    transformOrigin?: string;
    width?: string;
    height?: string;
    overflow?: string;
    className?: string;
  } | null>(null);
  const originalFirstChildStylesRef = useRef<{
    height?: string;
    minHeight?: string;
    maxHeight?: string;
    overflow?: string;
  } | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Always clean up any existing PowerPoint styles first
    const el: HTMLElement | null = target instanceof HTMLElement
      ? target
      : typeof target === 'string'
        ? document.getElementById(target)
        : null;

    // if (el) {
    //   // Clean up any existing PowerPoint styles
    //   el.className = '';
    //   el.style.position = '';
    //   el.style.left = '';
    //   el.style.top = '';
    //   el.style.transform = '';
    //   el.style.transformOrigin = '';
    //   el.style.width = '';
    //   el.style.height = '';
    //   el.style.overflow = '';
    // }

    // // Don't apply scaling on connect-to-stream page - check this first
    // if (pathname === '/connect-to-stream') {
    //   return;
    // }

    if (!el) return;
    observedElementRef.current = el;
    const measureEl: HTMLElement = el.parentElement || document.documentElement;
    measureElementRef.current = measureEl;

    if (!originalMeasureStylesRef.current) {
      originalMeasureStylesRef.current = {
        backgroundColor: measureEl.style.backgroundColor,
        display: measureEl.style.display,
        placeItems: (measureEl.style as any).placeItems,
        justifyContent: measureEl.style.justifyContent,
        alignItems: measureEl.style.alignItems,
      };
    }
    // Add black letterbox background around the scaled canvas
    if (measureEl.parentElement) measureEl.parentElement.style.backgroundColor = '#000000';
    // Center target using CSS grid without absolute positioning
    measureEl.style.display = 'grid';
    measureEl.style.height = '100vh';
    (measureEl.style as any).placeItems = 'center';
    if (!measureEl.style.justifyContent) measureEl.style.justifyContent = 'center';
    if (!measureEl.style.alignItems) measureEl.style.alignItems = 'center';

    if (!originalTargetStylesRef.current) {
      originalTargetStylesRef.current = {
        transform: el.style.transform,
        transformOrigin: el.style.transformOrigin,
        width: el.style.width,
        height: el.style.height,
        overflow: el.style.overflow,
        className: el.className,
      };
    }

    const applyScale = () => {
      // Use viewport dimensions for more reliable scaling
      const parentWidth = window.innerWidth || baselineWidth;
      const parentHeight = window.innerHeight || baselineHeight;
      const scaleX = parentWidth / baselineWidth;
      const scaleY = parentHeight / baselineHeight;
      const scale = Math.min(scaleX, scaleY);

      el.style.transformOrigin = 'left top';
      // Use absolute positioning to center the scaled content
      el.style.position = 'absolute';
      el.style.left = '50%';
      el.style.top = '50%';
      el.style.transform = `scale(${scale}) translate(-50%, -50%)`;
      el.style.width = `${baselineWidth}px`;
      el.style.height = `${baselineHeight}px`;
      el.style.overflow = 'hidden';
      el.className = 'h-full bg-grey-50 dark:bg-grey-900 ppt-stage';
    };

    applyScale();

    // Set up MutationObserver to watch for new dialog elements
    // mutationObserverRef.current = new MutationObserver((mutations) => {
    //   mutations.forEach((mutation) => {
    //     if (mutation.type === 'childList') {
    //       mutation.addedNodes.forEach((node) => {
    //         if (node instanceof HTMLElement) {
    //           // Check if the new element is a dialog
    //           const isDialog = node.matches('[role="dialog"]') ||
    //                           node.querySelector('[role="dialog"]');

    //           if (isDialog) {
    //             // Get current scale and apply to new dialogs immediately
    //             const currentScale = parseFloat(el.style.transform.replace('scale(', '').replace(')', '') || '1');
    //             const pptStage = document.getElementById('ppt-stage');
    //             if (pptStage) {
    //               const stageRect = pptStage.getBoundingClientRect();
    //               const scaleX = stageRect.width / baselineWidth;
    //               const scaleY = stageRect.height / baselineHeight;
    //               const dialogScale = Math.min(scaleX, scaleY) * 0.9;

    //               // Find and scale the new dialog
    //               const newDialog = node.matches('[role="dialog"]') ? node : node.querySelector('[role="dialog"]');
    //               if (newDialog instanceof HTMLElement) {
    //                 newDialog.style.transform = `scale(${dialogScale})`;
    //                 newDialog.style.transformOrigin = 'center center';
    //               }
    //             }
    //           }
    //         }
    //       });
    //     }
    //   });
    // });

    // Observe the entire document for new dialog elements
    // mutationObserverRef.current.observe(document.body, {
    //   childList: true,
    //   subtree: true
    // });

    // Observe container size changes — ResizeObserver covers window resize too
    resizeObserverRef.current = new ResizeObserver(applyScale);
    resizeObserverRef.current.observe(measureEl);
    resizeObserverRef.current.observe(document.documentElement);

    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;

      if (originalTargetStylesRef.current) {
        el.style.transform = originalTargetStylesRef.current.transform || '';
        el.style.transformOrigin = originalTargetStylesRef.current.transformOrigin || '';
        el.style.width = originalTargetStylesRef.current.width || '';
        el.style.height = originalTargetStylesRef.current.height || '';
        el.style.overflow = originalTargetStylesRef.current.overflow || '';
        el.className = originalTargetStylesRef.current.className || '';
        originalTargetStylesRef.current = null;
      }

      if (originalMeasureStylesRef.current && measureEl) {
        measureEl.style.backgroundColor = originalMeasureStylesRef.current.backgroundColor || '';
        measureEl.style.display = originalMeasureStylesRef.current.display || '';
        (measureEl.style as any).placeItems = originalMeasureStylesRef.current.placeItems || '';
        measureEl.style.justifyContent = originalMeasureStylesRef.current.justifyContent || '';
        measureEl.style.alignItems = originalMeasureStylesRef.current.alignItems || '';
        originalMeasureStylesRef.current = null;
      }

      observedElementRef.current = null;
      measureElementRef.current = null;
    };
  }, [target, baselineWidth, baselineHeight]);

  return null;
};

export default PowerPointView;
